import * as React from "react";
import { cn } from "@/lib/utils";

export const TEST_AUDIENCE = "test";

export type AudienceCategory = "users" | "sellers" | "buyers";

export type AudienceOption = {
  key: string;
  label: string;
  category: AudienceCategory;
  needs_city: boolean;
  /** People in the audience, or null while a city audience has no city chosen. */
  count: number | null;
  /** Optional second line, e.g. devices for push notifications. */
  detail?: string | undefined;
};

const TABS: { value: AudienceCategory; label: string; icon: string }[] = [
  { value: "users", label: "Users", icon: "👥" },
  { value: "sellers", label: "Sellers", icon: "🏪" },
  { value: "buyers", label: "Buyers", icon: "🛒" },
];

type Props = {
  audiences: AudienceOption[];
  value: string;
  onChange: (key: string) => void;
  city: string;
  onCityChange: (city: string) => void;
  cities: string[];
  /** Sending to one account is always available, above the tabs. */
  testSelected: boolean;
  onTestSelect: () => void;
};

/** Audience chooser shared by push notifications and emails. */
export function AudiencePicker({
  audiences,
  value,
  onChange,
  city,
  onCityChange,
  cities,
  testSelected,
  onTestSelect,
}: Props) {
  const selected = audiences.find((item) => item.key === value);
  const [tab, setTab] = React.useState<AudienceCategory>(selected?.category ?? "users");
  const visible = audiences.filter((item) => item.category === tab);

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={onTestSelect}
        aria-pressed={testSelected}
        className={cn(
          "flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-semibold",
          testSelected ? "border-primary bg-primary/5 text-primary" : "border-border hover:bg-muted/50",
        )}
      >
        <span>🧪 Test for one user</span>
        <span className="text-xs font-normal text-muted-foreground">One account</span>
      </button>

      <div role="tablist" aria-label="Audience category" className="grid grid-cols-3 gap-1 rounded-xl bg-muted p-1">
        {TABS.map((item) => (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={tab === item.value}
            onClick={() => setTab(item.value)}
            className={cn(
              "rounded-lg px-2 py-2 text-sm font-medium transition-colors",
              tab === item.value ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.icon} {item.label}
          </button>
        ))}
      </div>

      <div className="max-h-80 divide-y overflow-y-auto rounded-xl border" role="listbox" aria-label="Audiences">
        {visible.map((item) => {
          const active = !testSelected && value === item.key;
          return (
            <button
              key={item.key}
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => {
                onChange(item.key);
              }}
              className={cn(
                "flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm",
                active ? "bg-primary/5 font-semibold text-primary" : "hover:bg-muted/50",
              )}
            >
              <span className="min-w-0">
                <span className="block truncate">{item.label}</span>
                {item.detail && <span className="block text-xs font-normal text-muted-foreground">{item.detail}</span>}
              </span>
              <span className="shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold tabular-nums">
                {item.count === null ? "choose city" : item.count.toLocaleString("en-US")}
              </span>
            </button>
          );
        })}
        {visible.length === 0 && <p className="px-4 py-3 text-sm text-muted-foreground">No audiences here.</p>}
      </div>

      {!testSelected && selected?.needs_city && (
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="audience-city">
            City
          </label>
          <select
            id="audience-city"
            value={city}
            onChange={(event) => onCityChange(event.target.value)}
            className="h-10 w-full rounded-md border bg-background px-3 text-sm"
          >
            <option value="">Choose a city</option>
            {cities.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
